import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { HtmlTooltip } from "components/HeatMap";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

const UnitNewEdit = () => {
  usePageTitle("Edit Unit");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();

  const isEditMode = !!overlayData?.unitId;
  const allUnits = useMemo(() => overlayData?.allUnits || [], [overlayData?.allUnits]);

  // Edit lock for existing units only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "unittype",
    isEditMode ? overlayData?.unitId : null,
  );

  const [name, setName] = useState(overlayData?.name || "");
  const [abbreviation, setAbbreviation] = useState(
    overlayData?.abbreviation || "",
  );
  const [active, setActive] = useState(
    overlayData?.active !== undefined ? overlayData.active : true,
  );
  const [initialName] = useState(overlayData?.name || "");
  const [initialAbbreviation] = useState(overlayData?.abbreviation || "");
  const [initialActive] = useState(
    overlayData?.active !== undefined ? overlayData.active : true,
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [loadingDeleteCheck, setLoadingDeleteCheck] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const fetchDeleteCheck = useCallback(async () => {
    if (!overlayData?.unitId) return;

    setLoadingDeleteCheck(true);
    try {
      const response = await api.get(
        `/unitTypes/${overlayData.unitId}/delete-check`,
      );
      setDeleteCheck(response.data);
    } catch (err) {
      console.error("Error fetching delete check:", err);
    } finally {
      setLoadingDeleteCheck(false);
    }
  }, [overlayData?.unitId]);

  // Fetch delete check data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchDeleteCheck();
    }
  }, [isEditMode, overlayData?.unitId, fetchDeleteCheck]);

  // Check if unit name already exists (excluding current unit if editing)
  const isDuplicateName = useMemo(() => {
    const trimmedName = name.trim().toLowerCase();
    return allUnits.some(
      (unit) =>
        unit.Name.toLowerCase() === trimmedName &&
        unit.id !== overlayData?.unitId,
    );
  }, [name, allUnits, overlayData?.unitId]);

  // Check if abbreviation already exists (excluding current unit if editing)
  const isDuplicateAbbreviation = useMemo(() => {
    const trimmedAbbrev = abbreviation.trim().toLowerCase();
    return allUnits.some(
      (unit) =>
        unit.Abbreviation.toLowerCase() === trimmedAbbrev &&
        unit.id !== overlayData?.unitId,
    );
  }, [abbreviation, allUnits, overlayData?.unitId]);

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar("Unit name is required", theme, "error");
      return;
    }

    if (!abbreviation.trim()) {
      showSnackbar("Abbreviation is required", theme, "error");
      return;
    }

    if (isDuplicateName) {
      showSnackbar("This unit name already exists", theme, "error");
      return;
    }

    if (isDuplicateAbbreviation) {
      showSnackbar("This abbreviation already exists", theme, "error");
      return;
    }

    const nameChanged = name.trim() !== initialName.trim();
    const abbreviationChanged =
      abbreviation.trim() !== initialAbbreviation.trim();
    const activeChanged = active !== initialActive;

    if (!nameChanged && !abbreviationChanged && !activeChanged) {
      showSnackbar("No changes to save", theme, "info");
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        // Update existing unit
        await api.put(`/unitTypes/${overlayData.unitId}`, {
          Name: name.trim(),
          Abbreviation: abbreviation.trim(),
          Active: active,
        });
        showSnackbar("Unit updated successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "UnitType",
            recordId: overlayData.unitId,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      } else {
        // Create new unit
        const response = await api.post("/unitTypes", {
          Name: name.trim(),
          Abbreviation: abbreviation.trim(),
          Active: active,
        });
        showSnackbar("Unit created successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "UnitType",
            recordId: response.data.id,
            changeAction: "Created",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }

      // Trigger callback to refresh units list
      overlayData?.onUnitsSaved?.();
      setSaving(false);
      closeOverlay();
    } catch (err) {
      console.error(err);
      handleErrorDialog(
        err.response?.data?.error || "Error saving unit",
        theme,
      );
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!overlayData?.unitId) return;

    openDialog({
      theme: theme,
      title: "Deactivate Unit",
      message: `Are you sure you want to deactivate "${name}"? It will no longer be available for selection.`,
      primaryAction: "Deactivate",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        setDeleting(true);

        try {
          // Instead of deleting, we set Active = 0
          await api.put(`/unitTypes/${overlayData.unitId}`, {
            Name: name.trim(),
            Abbreviation: abbreviation.trim(),
            Active: false,
          });
          showSnackbar("Unit deactivated successfully", theme, "success");

          // Emit socket event for real-time updates
          if (socket) {
            socket.emit("settings:saved", {
              settingsType: "UnitType",
              recordId: overlayData.unitId,
              changeAction: "Deactivated",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          overlayData?.onUnitsSaved?.();
          closeDialog();
          closeOverlay();
        } catch (err) {
          console.error(err);
          closeDialog();
          handleErrorDialog(
            err.response?.data?.error || "Error deactivating unit",
            theme,
          );
        } finally {
          setDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const handleDeleteClick = () => {
    if (!deleteCheck?.canDelete) {
      showSnackbar(
        `Can't deactivate unit being used by ${deleteCheck.serviceCount} service${deleteCheck.serviceCount > 1 ? "s" : ""}`,
        theme,
        "error",
      );
      return;
    }
    handleDelete();
  };

  const renderDeleteTooltip = () => {
    if (!deleteCheck || deleteCheck.canDelete) return null;

    return (
      <Box>
        {deleteCheck.serviceCount > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              Services: {deleteCheck.serviceCount}
            </Typography>
            {deleteCheck.services.slice(0, 10).map((service) => (
              <Typography key={service.id} variant="body2">
                #{service.projectNumber}
              </Typography>
            ))}
            {deleteCheck.serviceCount > 10 && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontStyle="italic"
              >
                ...and {deleteCheck.serviceCount - 10} more
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const pageTitle = isEditMode ? "Edit Unit" : "New Unit";

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={closeOverlay}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "800px",
          maxHeight: "80%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {pageTitle}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isEditMode && active && (
              <HtmlTooltip
                title={deleteCheck?.canDelete ? "" : renderDeleteTooltip()}
                arrow
                placement="bottom"
              >
                <span>
                  {isMobile ? (
                    <IconButton
                      color="error"
                      onClick={handleDeleteClick}
                      disabled={deleting || loadingDeleteCheck}
                      sx={{
                        width: "40px",
                        height: "40px",
                        border: `2px solid ${theme.palette.error.main}`,
                        borderRadius: "12px",
                        ...(!deleteCheck?.canDelete && {
                          opacity: 0.5,
                          borderColor: theme.palette.action.disabled,
                        }),
                      }}
                    >
                      <DeleteRoundedIcon />
                    </IconButton>
                  ) : (
                    <Button
                      loading={deleting}
                      color="error"
                      variant="outlined"
                      size="large"
                      endIcon={<DeleteRoundedIcon />}
                      onClick={handleDeleteClick}
                      disabled={deleting || loadingDeleteCheck}
                      sx={{
                        borderRadius: "12px",
                        ...(!deleteCheck?.canDelete && {
                          opacity: 0.5,
                          color: theme.palette.action.disabled,
                          borderColor: theme.palette.action.disabled,
                        }),
                      }}
                    >
                      Deactivate
                    </Button>
                  )}
                </span>
              </HtmlTooltip>
            )}

            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={closeOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={closeOverlay}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Discard
              </Button>
            )}

            <Tooltip
              title={
                isMobile &&
                (saving || isDuplicateName || isDuplicateAbbreviation)
                  ? "Please fix errors before saving"
                  : ""
              }
              placement="top"
            >
              <span>
                {isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleSave}
                    disabled={
                      saving || isDuplicateName || isDuplicateAbbreviation
                    }
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                ) : (
                  <Button
                    loading={saving}
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={
                      saving || isDuplicateName || isDuplicateAbbreviation
                    }
                    sx={{
                      borderRadius: "12px",
                    }}
                  >
                    Save
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Unit Details
              </Typography>
            </Grid>

            {isDuplicateName && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">
                  This unit name already exists in the system.
                </Alert>
              </Grid>
            )}

            {isDuplicateAbbreviation && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">
                  This abbreviation already exists in the system.
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Unit Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 100 }}
                placeholder="e.g., Words, Pages, Hours"
                error={isDuplicateName}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Abbreviation"
                value={abbreviation}
                onChange={(e) => setAbbreviation(e.target.value)}
                inputProps={{ maxLength: 20 }}
                placeholder="e.g., words, pgs, hrs"
                error={isDuplicateAbbreviation}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1">
                    Active{" "}
                    {active
                      ? "(Unit is available for selection)"
                      : "(Unit is hidden from selection)"}
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default UnitNewEdit;
